<script module lang="ts">
	// UC-12. Settings → Printing: the account's one customer receipt and one
	// kitchen ticket, shared by every shop. Each tab edits its template beside
	// a preview the server draws with the printer's own renderer and fonts, so
	// the image is the printer's dots, never a browser layout; the paper strip
	// around it is the browser's (ReceiptPreview). The shop and paper width
	// only choose the sample; they are not settings.
	//
	// The customer receipt is its sections in print order (#637). Which kind
	// takes which control, and which move keeps the order, are the account's
	// rules, mirrored in src/domain/receiptSections.ts and held to the server's
	// own results; this screen only uses them, and the server judges every save.

	import type { MessageKey } from '../i18n/messages';
	import {
		canAdd,
		canHide,
		LOGO_PERCENT_STEP,
		MAX_LOGO_PERCENT,
		MIN_LOGO_PERCENT,
		moveSection,
		removable,
		takesBold,
		takesCenter,
		type ReceiptSection,
		type SectionKind,
	} from '../domain/receiptSections';

	/**
	 * A switch on a template: its key, its words, and whether it is on while
	 * the key is false (`inverted`), as Option prices is on while
	 * `hideChoicePrices` is not true (#949).
	 */
	export type Flag = readonly [key: string, label: MessageKey, inverted?: 'inverted'];
	export type Section = ReceiptSection;

	export const receiptFlags: Flag[] = [
		['roomy', 'printingRoomy'],
		['details', 'printingDetails'],
		['hideChoicePrices', 'printingOptionPrices', 'inverted'],
		['unitPrices', 'printingUnitPrices'],
		['lineTotals', 'printingLineTotals'],
		['automatic', 'printingAutomatic'],
	];
	export const kitchenFlags: Flag[] = [
		['roomy', 'printingRoomy'],
		['separators', 'printingSeparators'],
		['largeIdentifiers', 'printingIdentifiers'],
		['prominentType', 'printingType'],
		['boldInstructions', 'printingInstructions'],
		['groupIdentical', 'printingGroupIdentical'],
		['groupCategories', 'printingGroupCategories'],
	];

	/** The flags of template that are on; an inverted one is on while its key is not true. */
	export function flagsOn(template: object, flags: Flag[]): string[] {
		const values = template as Record<string, unknown>;
		return flags
			.filter(([key, , inverted]) => (inverted ? values[key] !== true : values[key] === true))
			.map(([key]) => key);
	}

	/**
	 * Sets each of flags on template to whether picked names it. An inverted
	 * flag's key is set true only while its switch is off, and is otherwise
	 * taken off the template, so a save sends it only when set: an older API
	 * refuses a key it does not know.
	 */
	export function applyFlags(template: object, flags: Flag[], picked: string[]): void {
		const values = template as Record<string, unknown>;
		for (const [key, , inverted] of flags) {
			if (!inverted) values[key] = picked.includes(key);
			else if (picked.includes(key)) delete values[key];
			else values[key] = true;
		}
	}

	/**
	 * Whether the logo's − (-1) or + (+1) acts. From an unset width (0 or
	 * absent, the logo at its own size) only + acts, since − could still grow
	 * a small logo to 50%; then each holds at MIN_LOGO_PERCENT and
	 * MAX_LOGO_PERCENT.
	 */
	export function logoCanStep(percent: number | undefined, direction: -1 | 1): boolean {
		if (!percent) return direction > 0;
		return direction > 0 ? percent < MAX_LOGO_PERCENT : percent > MIN_LOGO_PERCENT;
	}

	/**
	 * The logo's width after one tap of − (-1) or + (+1), or undefined while
	 * it stays unset: from its own size + sets 50% and − changes nothing.
	 * Each later tap steps by LOGO_PERCENT_STEP, held between
	 * MIN_LOGO_PERCENT and MAX_LOGO_PERCENT, onto a step when it was off one.
	 */
	export function stepLogoWidth(
		percent: number | undefined,
		direction: -1 | 1,
	): number | undefined {
		if (!percent) return direction > 0 ? 50 : undefined;
		const onStep =
			direction > 0
				? Math.floor(percent / LOGO_PERCENT_STEP) * LOGO_PERCENT_STEP + LOGO_PERCENT_STEP
				: Math.ceil(percent / LOGO_PERCENT_STEP) * LOGO_PERCENT_STEP - LOGO_PERCENT_STEP;
		return Math.min(MAX_LOGO_PERCENT, Math.max(MIN_LOGO_PERCENT, onStep));
	}

	/**
	 * A percentage in words such as "50%". Only "%1" is filled: fill reads a
	 * bare "%" as a placeholder too, and here it is the percent sign.
	 */
	export function percentLabel(words: string, percent: number): string {
		return words.replace('%1', String(percent));
	}

	/** A new text line (shown, centred, empty) or separator (dashed, no space). */
	export function newSection(kind: 'text' | 'separator'): Section {
		return kind === 'text'
			? { kind, visible: true, center: true, bold: false, text: '' }
			: { kind, visible: true, style: 'dashed', spaceMM: 0 };
	}

	/**
	 * How many rows a text line's field needs: none (one line) for a line
	 * typed here, or one per line for one that holds line breaks, as settings
	 * saved before sections turn a multi-line header or footer into one text
	 * line. Its breaks print as lines, so the field must keep them.
	 */
	export function textRows(text: string | undefined): number | undefined {
		const lines = (text ?? '').split('\n').length;
		return lines > 1 ? lines : undefined;
	}

	/**
	 * What the live region says next. A polite region speaks only when its
	 * words change, so saying the same words again, a second refusal, adds a
	 * no-break space after them, and the next time takes it away.
	 */
	export function nextSpoken(previous: string, words: string): string {
		return previous === words ? words + '\u00a0' : words;
	}

	const names: Record<SectionKind, MessageKey> = {
		logo: 'printingLogo',
		shopName: 'printingShopName',
		shopAddress: 'printingShopAddress',
		shopPhone: 'printingShopPhone',
		orderDetails: 'printingOrderDetails',
		items: 'printingItems',
		totals: 'printingTotals',
		payment: 'printingPayment',
		qr: 'printingQR',
		text: 'printingTextLine',
		separator: 'printingSeparator',
	};

	/** The words that name a section's row. */
	export function sectionName(kind: SectionKind): MessageKey {
		return names[kind];
	}

	const warningWords: Record<string, MessageKey> = {
		catalogue_unpublished: 'printingUnpublished',
		logo_missing: 'printingNoLogo',
		logo_unavailable: 'printingLogoUnreadable',
	};

	/**
	 * The print logo's link from the printing view an upload or a removal
	 * answers (#1056). The panel takes this alone from that answer: the
	 * settings the page holds, saved or not, and their revision stay as they
	 * are. A link missing from the answer, from an older server, reads as no
	 * print logo.
	 */
	export function printLogoOf(answer: { logoUrl?: string } | null | undefined): string {
		return answer?.logoUrl ?? '';
	}

	/** The words that explain a preview's warning; a warning this screen does
	 * not know, from a later server, has none and shows nothing. */
	export function warningMessage(warning: string): MessageKey | undefined {
		return Object.hasOwn(warningWords, warning) ? warningWords[warning] : undefined;
	}

	/**
	 * Keeps only the newest preview: each edit takes a ticket, and an answer
	 * counts only while its ticket is still the newest, so a slow answer to an
	 * older edit never shows as the current preview.
	 */
	export class Latest {
		private count = 0;
		take(): number {
			return ++this.count;
		}
		drop(): void {
			this.count++;
		}
		current(ticket: number): boolean {
			return ticket === this.count;
		}
	}
</script>

<script lang="ts">
	import { tick } from 'svelte';
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import ImageInput, { type Picked } from '../components/ImageInput.svelte';
	import ReceiptPreview from '../components/ReceiptPreview.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { api } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	import { leftToRight } from '../format/bidi';
	import { arrowStep, dropAmong, edgeScroll, GripDrag } from '../till/reorder';
	import { commitNow, flash, removeWithUndo } from '../owner/notices.svelte';
	import { MAX_SPACE_MM, MAX_TEXT_RUNES } from '../domain/receiptSections';
	import type { components } from '../api/types';

	type Config = components['schemas']['PrintSettings'];

	interface Props {
		language: Language;
	}

	let { language }: Props = $props();

	let config = $state<Config | null>(null);
	let shops = $state<{ id: string; name: string }[]>([]);
	let shopId = $state('');
	let document = $state<'receipt' | 'kitchen_slip'>('receipt');
	let paperMM = $state<58 | 80>(80);
	let failure = $state('');
	let previewFailure = $state('');
	let preview = $state('');
	let warnings = $state<string[]>([]);
	let updating = $state(false);
	let saving = $state(false);
	let saved = $state(false);
	const latest = new Latest();
	// The print logo (UC-12.R14): its link, kept apart from the settings the
	// page edits; whether it left the row while its undo runs; and a count of
	// its changes, so the preview draws again after each.
	let printLogo = $state('');
	let printLogoGone = $state(false);
	let printLogoChanges = $state(0);
	let uploading = $state(false);
	let logoProblem = $state<null | 'type' | 'size' | 'small'>(null);
	let logoFailure = $state('');
	// The removal sent once its undo lapsed, while its answer is coming.
	let removing: Promise<void> | null = null;
	const shownLogo = $derived(printLogoGone ? '' : printLogo);

	$effect(() => {
		let active = true;
		void Promise.all([api.GET('/api/owner/printing'), api.GET('/api/owner/shops')])
			.then(([printing, owned]) => {
				if (!active) return;
				if (!printing.data || !owned.data) {
					failure =
						printing.error?.message ?? owned.error?.message ?? message(language, 'loadFailed');
					return;
				}
				config = printing.data.settings;
				printLogo = printLogoOf(printing.data);
				shops = owned.data.shops ?? [];
				shopId = shops[0]?.id ?? '';
			})
			.catch(() => {
				if (active) failure = message(language, 'loadFailed');
			});
		return () => {
			active = false;
		};
	});

	const template = $derived(
		config ? (document === 'receipt' ? config.receipt : config.kitchen) : null,
	);
	const flags = $derived(document === 'receipt' ? receiptFlags : kitchenFlags);
	const picks = $derived(template ? flagsOn(template, flags) : []);
	const sections = $derived(config?.receipt.sections ?? []);

	// Every edit, shop, tab or width asks for a new preview; until it comes,
	// no older image shows as current.
	$effect(() => {
		const settings = config ? $state.snapshot(config) : null;
		const shop = shopId;
		const kind = document;
		const paper = paperMM;
		// The server reads the print logo itself; a change to it draws again.
		void printLogoChanges;
		const ticket = latest.take();
		preview = '';
		warnings = [];
		previewFailure = '';
		if (!settings || !shop) {
			updating = false;
			return;
		}
		updating = true;
		const timer = setTimeout(() => {
			api
				.POST('/api/owner/printing/preview', {
					body: { settings, shopId: shop, document: kind, paperMM: paper },
				})
				.then(({ data, error }) => {
					if (!latest.current(ticket)) return;
					if (!data) {
						previewFailure = error?.message ?? message(language, 'loadFailed');
						return;
					}
					preview = 'data:image/png;base64,' + data.png;
					warnings = data.warnings ?? [];
				})
				.catch(() => {
					if (latest.current(ticket)) previewFailure = message(language, 'loadFailed');
				})
				.finally(() => {
					if (latest.current(ticket)) updating = false;
				});
		}, 180);
		return () => {
			clearTimeout(timer);
			latest.drop();
		};
	});

	function edited() {
		saved = false;
	}

	// "80 mm" in a left-to-right isolate, so the number stays before its
	// unit inside Arabic.
	function paperWidth(mm: 58 | 80): string {
		return leftToRight(fill(message(language, 'printingPaperWidth'), mm));
	}

	// Each row keeps one key for as long as the screen holds it, so a moved
	// row stays the same element and its test id names it wherever it sits.
	// A section carries no id of its own, and the contract takes none.
	const keys = new WeakMap<Section, number>();
	let nextKey = 0;
	function keyOf(section: Section): number {
		let key = keys.get(section);
		if (key === undefined) {
			key = nextKey++;
			keys.set(section, key);
		}
		return key;
	}
	// Whether a text line edits in several rows is decided once, when the row
	// first shows: a line that held breaks keeps a multi-line field even when
	// the owner deletes them, so the field never swaps under the caret.
	const rowsOf = new WeakMap<Section, number | undefined>();
	function rowsFor(section: Section): number | undefined {
		if (!rowsOf.has(section)) rowsOf.set(section, textRows(section.text));
		return rowsOf.get(section);
	}
	function rowId(section: Section): string {
		return removable(section.kind) ? `${section.kind}-${keyOf(section)}` : section.kind;
	}

	// The refusal stays on screen until the next change; the row that was
	// refused is marked for a moment, and both are said aloud.
	let refused = $state(false);
	let refusedRow = $state<Section | null>(null);
	let announcement = $state('');

	function setSections(next: Section[]) {
		if (!config) return;
		config.receipt.sections = next;
		edited();
	}

	interface Stepper {
		id: string;
		/** The row's name, after each button's spoken label. */
		name: string;
		valueTestId: string;
		value: string;
		/** A number and its unit, kept left to right. */
		ltr: boolean;
		less: string;
		more: string;
		lessDisabled: boolean;
		moreDisabled: boolean;
		onLess: () => void;
		onMore: () => void;
	}

	// An unset width takes the key off the section, so a save never sends it.
	function setLogoWidth(section: Section, percent: number | undefined) {
		if (percent) section.widthPercent = percent;
		else delete section.widthPercent;
	}

	function change(update: () => void) {
		update();
		refused = false;
		edited();
	}

	function refuse(section: Section) {
		refused = true;
		refusedRow = section;
		announcement = nextSpoken(announcement, message(language, 'printingRefusedLive'));
		setTimeout(() => {
			if (refusedRow === section) refusedRow = null;
		}, 900);
	}

	function announceMoved(section: Section) {
		const at = sections.indexOf(section);
		announcement = nextSpoken(
			announcement,
			fill(
				message(language, 'printingSectionMoved'),
				message(language, sectionName(section.kind)),
				at + 1,
				sections.length,
			),
		);
	}

	async function focusGrip(section: Section) {
		await tick();
		list?.querySelector<HTMLElement>(`[data-testid="printing-grip-${rowId(section)}"]`)?.focus();
	}

	function add(kind: 'text' | 'separator') {
		if (!canAdd(sections, kind)) return;
		change(() => setSections([...sections, newSection(kind)]));
	}

	function remove(section: Section) {
		change(() => setSections(sections.filter((s) => s !== section)));
	}

	// A row moves by its grip alone: dragged with a mouse, pen or finger, or
	// one place at a time with the arrow keys on the focused grip. There are
	// no Up and Down buttons on the dashboard (owner, #637); they stay the
	// till's. The list reflows under the pointer while the order holds; over
	// a place that breaks it, the row is marked refused, and dropped there it
	// goes back to where the drag began.
	let list = $state<HTMLElement>();
	let dragging = $state<Section | null>(null);
	let start: Section[] = [];
	let blocked = false;

	function follow(event: PointerEvent) {
		const section = dragging;
		if (!section || !list) return;
		const others = [...list.querySelectorAll<HTMLElement>('[data-section-key]')].filter(
			(row) => row.dataset.sectionKey !== String(keyOf(section)),
		);
		const from = sections.indexOf(section);
		const to = dropAmong(event.clientY, others);
		if (from >= 0 && to !== from) {
			const next = moveSection(sections, from, to);
			if (next) {
				blocked = false;
				refusedRow = null;
				setSections(next);
			} else {
				blocked = true;
				refusedRow = section;
			}
		} else if (from === to) {
			blocked = false;
			refusedRow = null;
		}
		const by = edgeScroll(event.clientY, 0, window.innerHeight);
		if (by !== 0) window.scrollBy(0, by);
	}

	function drop() {
		const section = dragging;
		dragging = null;
		if (!section) return;
		if (blocked) {
			setSections(start);
			refuse(section);
		} else if (sections.indexOf(section) !== start.indexOf(section)) {
			refused = false;
			announceMoved(section);
		}
		blocked = false;
	}

	// Followed on the window, like Edit my items' drag (GripDrag says why).
	const drag = new GripDrag(follow, drop);

	function lift(section: Section, event: PointerEvent) {
		if (!drag.start(event)) return;
		dragging = section;
		start = [...sections];
		blocked = false;
	}

	function key(section: Section, event: KeyboardEvent) {
		const step = arrowStep(event);
		if (step === 0) return;
		const from = sections.indexOf(section);
		const to = from + step;
		if (to < 0 || to >= sections.length) return;
		const next = moveSection(sections, from, to);
		if (!next) {
			refuse(section);
			return;
		}
		refused = false;
		setSections(next);
		announceMoved(section);
		void focusGrip(section);
	}

	$effect(() => () => drag.stop());

	const logoWords: Record<'type' | 'size' | 'small', MessageKey> = {
		type: 'logoType',
		size: 'logoSize',
		small: 'logoSmall',
	};

	// The print logo saves at once, apart from Save (the owner: "Save at
	// once"), and the panel takes only its link from the answer.
	async function pickPrintLogo(picked: Picked) {
		if (picked.url) URL.revokeObjectURL(picked.url);
		logoProblem = picked.problem;
		logoFailure = '';
		if (picked.problem) return;
		uploading = true;
		try {
			// A removal still waiting for its undo is sent first, and one
			// already sent is waited for, so it can never delete the logo
			// this upload stores.
			await commitNow();
			await removing;
			const { data, error } = await api.PUT('/api/owner/printing/logo', {
				// The body is the file itself, sent as it is; the generated
				// type calls a binary body a string.
				body: picked.file as unknown as string,
				bodySerializer: (file) => file,
				headers: { 'Content-Type': 'application/octet-stream' },
			});
			if (error !== undefined || !data) {
				const reason = String(error?.details?.reason ?? '');
				if (error?.code === 'IMAGE_INVALID' && reason in logoWords) {
					logoProblem = reason as 'type' | 'size' | 'small';
				} else {
					logoFailure = error?.message ?? message(language, 'loadFailed');
				}
				return;
			}
			printLogoGone = false;
			printLogo = printLogoOf(data);
			printLogoChanges++;
			flash(message(language, 'saved'));
		} catch {
			logoFailure = message(language, 'loadFailed');
		} finally {
			uploading = false;
		}
	}

	// The logo leaves the row at once with its undo; the removal is sent only
	// when the undo lapses, and the panel then reads the link again.
	function removePrintLogo() {
		logoProblem = null;
		logoFailure = '';
		printLogoGone = true;
		removeWithUndo(
			message(language, 'printingLogoRemoved'),
			() => (removing = sendRemoval().finally(() => (removing = null))),
			() => (printLogoGone = false),
		);
	}

	// A refused or failed removal is said under the logo, as a failed upload
	// is; either way the row then shows the logo the server keeps.
	async function sendRemoval() {
		try {
			const { error } = await api.DELETE('/api/owner/printing/logo');
			if (error !== undefined) logoFailure = error?.message ?? message(language, 'loadFailed');
		} catch {
			logoFailure = message(language, 'loadFailed');
		}
		try {
			const { data } = await api.GET('/api/owner/printing');
			if (data) printLogo = printLogoOf(data);
		} catch {
			// The row keeps the link it had; the next load reads it.
		}
		printLogoGone = false;
		printLogoChanges++;
	}

	async function save() {
		if (!config || saving) return;
		saving = true;
		failure = '';
		saved = false;
		try {
			const { data, error } = await api.PUT('/api/owner/printing', {
				body: $state.snapshot(config),
			});
			if (!data) {
				failure = error?.message ?? message(language, 'loadFailed');
				return;
			}
			saved = true;
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			saving = false;
		}
	}
</script>

<!-- A − value + stepper: a separator's space, or the logo's width. -->
{#snippet stepper(step: Stepper)}
	<span class="stepper">
		<Button
			testId="printing-less-{step.id}"
			label=""
			ariaLabel="{step.less}: {step.name}"
			title={step.less}
			icon="minus"
			square
			size="sm"
			tone="secondary"
			disabled={step.lessDisabled}
			onPress={step.onLess}
		/>
		{#if step.ltr}
			<bdi class="value" dir="ltr" data-testid={step.valueTestId}>{step.value}</bdi>
		{:else}
			<span class="value words" data-testid={step.valueTestId}>{step.value}</span>
		{/if}
		<Button
			testId="printing-more-{step.id}"
			label=""
			ariaLabel="{step.more}: {step.name}"
			title={step.more}
			icon="plus"
			square
			size="sm"
			tone="secondary"
			disabled={step.moreDisabled}
			onPress={step.onMore}
		/>
	</span>
{/snippet}

<div class="printing" data-testid="panel-printing">
	{#if failure}
		<Band tone="flag" testId="printing-error">{failure}</Band>
	{/if}
	{#if config && template}
		<Segmented
			label={message(language, 'printingTitle')}
			value={document}
			options={[
				{
					value: 'receipt',
					label: message(language, 'printingReceipt'),
					testId: 'printing-tab-receipt',
				},
				{
					value: 'kitchen_slip',
					label: message(language, 'printingKitchen'),
					testId: 'printing-tab-kitchen',
				},
			]}
			onChange={(v) => (document = v as typeof document)}
		/>
		<div class="columns">
			<div class="controls">
				<div class="group">
					<span>{message(language, 'printingLanguage')}</span>
					<Segmented
						label={message(language, 'printingLanguage')}
						value={template.language}
						options={[
							{
								value: 'ar',
								label: message(language, 'settingsLanguageArabic'),
								testId: 'printing-language-ar',
							},
							{
								value: 'msa',
								label: message(language, 'settingsLanguageMSA'),
								testId: 'printing-language-msa',
							},
							{
								value: 'en',
								label: message(language, 'settingsLanguageEnglish'),
								testId: 'printing-language-en',
							},
						]}
						onChange={(v) => {
							if (template) template.language = v as Language;
							edited();
						}}
					/>
				</div>
				<div class="group">
					<span>{message(language, 'printingFont')}</span>
					<Segmented
						label={message(language, 'printingFont')}
						value={template.font}
						options={[
							{
								value: 'normal',
								label: message(language, 'printingNormal'),
								testId: 'printing-font-normal',
							},
							{
								value: 'large',
								label: message(language, 'printingLarge'),
								testId: 'printing-font-large',
							},
						]}
						onChange={(v) => {
							if (template) template.font = v as 'normal' | 'large';
							edited();
						}}
					/>
				</div>
				<Choice
					mode="multi"
					stack
					value={picks}
					options={flags.map(([key, label]) => ({
						value: key,
						label: message(language, label),
						testId: 'printing-option-' + key,
					}))}
					onChange={(values) => {
						if (template && Array.isArray(values)) applyFlags(template, flags, values);
						edited();
					}}
					testId="printing-options"
				/>
				{#if document === 'receipt'}
					<p class="note">{message(language, 'printingItemsNote')}</p>
					<section class="order" data-testid="printing-order">
						<h3 class="heading">{message(language, 'printingOrder')}</h3>
						<p class="note">{message(language, 'printingOrderNote')}</p>
						{#if refused}
							<Band tone="flag" testId="printing-refused"
								>{message(language, 'printingRefused')}</Band
							>
						{/if}
						<div class="list" role="list" bind:this={list} data-testid="printing-sections">
							{#each sections as section (keyOf(section))}
								{@const id = rowId(section)}
								{@const name = message(language, sectionName(section.kind))}
								<div
									class="section"
									role="listitem"
									data-testid="printing-section-{id}"
									data-section-key={keyOf(section)}
									data-kind={section.kind}
									data-hidden={section.visible ? 'false' : 'true'}
									data-dragging={dragging === section ? 'true' : 'false'}
									data-refused={refusedRow === section ? 'true' : 'false'}
								>
									<div class="head">
										<Button
											testId="printing-grip-{id}"
											label=""
											ariaLabel="{message(language, 'printingDrag')}: {name}"
											title={message(language, 'printingDrag')}
											icon="grip"
											tone="text-quiet"
											size="sm"
											grip={{
												onPointerDown: (event) => lift(section, event),
												onKeyDown: (event) => key(section, event),
											}}
										/>
										<span class="name">{name}</span>
										<!-- From the row's end towards its start: centre, visible, bold,
										     delete, then a separator's style and spacing, the logo's
										     width, or the order details' one line. -->
										<span class="tools">
											{#if takesCenter(section.kind)}
												<Button
													testId="printing-center-{id}"
													label=""
													ariaLabel="{message(language, 'printingCenter')}: {name}"
													title={message(language, 'printingCenter')}
													icon="center"
													square
													size="sm"
													tone={section.center ? 'ink' : 'secondary'}
													pressed={Boolean(section.center)}
													onPress={() => change(() => (section.center = !section.center))}
												/>
											{/if}
											{#if canHide(section.kind)}
												{@const shownLabel = message(
													language,
													section.visible ? 'printingShown' : 'printingHidden',
												)}
												<Button
													testId="printing-visible-{id}"
													label=""
													ariaLabel="{shownLabel}: {name}"
													title={shownLabel}
													icon={section.visible ? 'visible' : 'hidden'}
													square
													size="sm"
													tone={section.visible ? 'ink' : 'secondary'}
													pressed={section.visible}
													onPress={() => change(() => (section.visible = !section.visible))}
												/>
											{/if}
											{#if takesBold(section.kind)}
												<Button
													testId="printing-bold-{id}"
													label=""
													ariaLabel="{message(language, 'printingBold')}: {name}"
													title={message(language, 'printingBold')}
													icon="bold"
													square
													size="sm"
													tone={section.bold ? 'ink' : 'secondary'}
													pressed={Boolean(section.bold)}
													onPress={() => change(() => (section.bold = !section.bold))}
												/>
											{/if}
											{#if removable(section.kind)}
												<Button
													testId="printing-delete-{id}"
													label=""
													ariaLabel="{message(language, 'printingDelete')}: {name}"
													title={message(language, 'printingDelete')}
													icon="bin"
													square
													size="sm"
													tone="secondary"
													onPress={() => remove(section)}
												/>
											{/if}
											{#if section.kind === 'separator'}
												{@const blank = section.style === 'blank'}
												{@const styleLabel = message(
													language,
													blank ? 'printingBlank' : 'printingDashed',
												)}
												{@const space = section.spaceMM ?? 0}
												<Button
													testId="printing-style-{id}"
													label=""
													ariaLabel="{styleLabel}: {name}"
													title={styleLabel}
													icon={blank ? 'gap' : 'dashed'}
													square
													size="sm"
													tone="secondary"
													onPress={() => change(() => (section.style = blank ? 'dashed' : 'blank'))}
												/>
												{@render stepper({
													id,
													name,
													valueTestId: `printing-space-${id}`,
													value: fill(message(language, 'printingSpace'), space),
													ltr: true,
													less: message(language, 'printingLessSpace'),
													more: message(language, 'printingMoreSpace'),
													lessDisabled: space <= 0,
													moreDisabled: space >= MAX_SPACE_MM,
													onLess: () => change(() => (section.spaceMM = Math.max(0, space - 1))),
													onMore: () =>
														change(() => (section.spaceMM = Math.min(MAX_SPACE_MM, space + 1))),
												})}
											{/if}
											{#if section.kind === 'logo'}
												{@const width = section.widthPercent ?? 0}
												{@render stepper({
													id,
													name,
													valueTestId: `printing-width-${id}`,
													value: width
														? percentLabel(message(language, 'printingWidth'), width)
														: message(language, 'printingOriginalSize'),
													ltr: width > 0,
													less: message(language, 'printingSmallerLogo'),
													more: message(language, 'printingLargerLogo'),
													lessDisabled: !logoCanStep(width, -1),
													moreDisabled: !logoCanStep(width, 1),
													onLess: () =>
														change(() => setLogoWidth(section, stepLogoWidth(width, -1))),
													onMore: () =>
														change(() => setLogoWidth(section, stepLogoWidth(width, 1))),
												})}
												{#if width}
													<Button
														testId="printing-width-reset-{id}"
														label=""
														ariaLabel="{message(language, 'printingOriginalSize')}: {name}"
														title={message(language, 'printingOriginalSize')}
														icon="close"
														square
														size="sm"
														tone="secondary"
														onPress={() => change(() => setLogoWidth(section, undefined))}
													/>
												{/if}
											{/if}
											{#if section.kind === 'orderDetails'}
												<Button
													testId="printing-oneline-{id}"
													label=""
													ariaLabel="{message(language, 'printingOneLine')}: {name}"
													title={message(language, 'printingOneLine')}
													icon="oneline"
													square
													size="sm"
													tone={section.oneLine ? 'ink' : 'secondary'}
													pressed={Boolean(section.oneLine)}
													onPress={() => change(() => (section.oneLine = !section.oneLine))}
												/>
											{/if}
										</span>
									</div>
									{#if section.kind === 'logo'}
										<div class="logo" data-testid="printing-logo-upload">
											<span class="label">{message(language, 'printingLogoUpload')}</span>
											<ImageInput
												testId="printing-logo"
												src={shownLogo || undefined}
												alt={message(language, 'printingLogoUpload')}
												emptyLabel={message(language, 'logoEmpty')}
												addLabel={message(language, 'logoAdd')}
												replaceLabel={message(language, 'logoReplace')}
												removeLabel={message(language, 'logoRemove')}
												onPick={(picked) => void pickPrintLogo(picked)}
												onRemove={removePrintLogo}
												state={uploading ? 'pending' : 'idle'}
												pendingLabel={message(language, 'logoUploading')}
												error={logoProblem
													? message(language, logoWords[logoProblem])
													: logoFailure || undefined}
												note={message(language, 'logoNote')}
											/>
										</div>
									{/if}
									{#if section.kind === 'text'}
										<Field
											testId="printing-line-{id}"
											ariaLabel={name}
											value={section.text ?? ''}
											rows={rowsFor(section)}
											maxCharacters={MAX_TEXT_RUNES}
											onInput={(v) => change(() => (section.text = v))}
										/>
									{/if}
								</div>
							{/each}
						</div>
						<div class="add">
							<Button
								testId="printing-add-text"
								label={message(language, 'printingAddText')}
								icon="plus"
								tone="secondary"
								size="sm"
								disabled={!canAdd(sections, 'text')}
								onPress={() => add('text')}
							/>
							<Button
								testId="printing-add-separator"
								label={message(language, 'printingAddSeparator')}
								icon="plus"
								tone="secondary"
								size="sm"
								disabled={!canAdd(sections, 'separator')}
								onPress={() => add('separator')}
							/>
						</div>
						<p class="spoken" aria-live="polite" data-testid="printing-announcement">
							{announcement}
						</p>
					</section>
				{/if}
				<Button
					testId="printing-save"
					label={message(language, 'save')}
					state={saving ? 'pending' : 'idle'}
					pendingLabel={message(language, 'saving')}
					onPress={() => void save()}
				/>
				{#if saved}
					<p role="status" data-testid="printing-saved">{message(language, 'printingSaved')}</p>
				{/if}
			</div>
			<Card
				title={message(language, 'printingSample')}
				note={message(language, 'printingSampleNote')}
				testId="printing-preview-card"
			>
				<div class="preview">
					<Choice
						title={message(language, 'printingShop')}
						value={shopId}
						options={shops.map((s) => ({
							value: s.id,
							label: s.name,
							testId: 'printing-preview-shop-' + s.id,
						}))}
						onChange={(v) => (shopId = String(v))}
						testId="printing-preview-shop"
					/>
					<div class="group">
						<span>{message(language, 'printingPaper')}</span>
						<Segmented
							label={message(language, 'printingPaper')}
							value={String(paperMM)}
							options={[
								{ value: '58', label: paperWidth(58), testId: 'printing-paper-58' },
								{ value: '80', label: paperWidth(80), testId: 'printing-paper-80' },
							]}
							onChange={(v) => (paperMM = Number(v) as 58 | 80)}
						/>
					</div>
					{#if updating}
						<p role="status" data-testid="printing-preview-pending">
							{message(language, 'printingUpdating')}
						</p>
					{/if}
					{#if previewFailure}
						<Band tone="flag" testId="printing-preview-error">{previewFailure}</Band>
					{/if}
					{#each warnings as warning (warning)}
						{@const words = warningMessage(warning)}
						{#if words}
							<Band tone="flag" testId={'printing-warning-' + warning}>
								{message(language, words)}
							</Band>
						{/if}
					{/each}
					{#if preview}
						<ReceiptPreview testId="printing-preview" {paperMM}>
							<img
								src={preview}
								alt={message(language, 'printingSample')}
								data-testid="printing-preview-image"
							/>
						</ReceiptPreview>
					{/if}
				</div>
			</Card>
		</div>
	{/if}
</div>

<style>
	.printing {
		padding: var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.columns {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--space-7);
		align-items: start;
	}

	.controls,
	.preview {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
		min-inline-size: 0;
	}

	.preview {
		padding: var(--card-pad);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	/* The printer's dots, scaled smoothly to the printed width of the paper. */
	img {
		display: block;
		inline-size: 100%;
		block-size: auto;
	}

	.note {
		font-size: var(--text-2xs);
		color: var(--text-muted);
		margin: 0;
	}

	.order {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.heading {
		margin: 0;
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.list {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	/* One container and one padding for every kind; a text line's field sits
	 * inside it, under the header row. A dragged row lifts and a refused one
	 * shows a dashed edge, both from interaction.css. */
	.section {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		padding: var(--space-2);
		background: var(--surface-page);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-input);
	}

	/* In a narrow column the controls go under the name rather than past
	 * the row's edge, still from the row's end, in the same order. */
	.head {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2);
		min-block-size: var(--tap-min);
	}

	.name {
		flex: 1 1 5em;
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.section[data-hidden='true'] .name {
		color: var(--text-muted);
		text-decoration: line-through;
	}

	.tools {
		flex: 0 1 auto;
		display: flex;
		flex-direction: row-reverse;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-1);
		margin-inline-start: auto;
	}

	.stepper {
		display: flex;
		align-items: center;
		gap: var(--space-1);
	}

	.value {
		min-inline-size: 3.6em;
		text-align: center;
		font-family: var(--font-mono);
		font-size: var(--text-xs);
	}

	/* Words, such as Original size, in the value's place. */
	.value.words {
		font-family: inherit;
		color: var(--text-muted);
	}

	/* The print logo sits inside its row under the header row, where a text
	 * line's field sits. */
	.logo {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.add {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
	}

	/* Said aloud, never shown: the new place of a moved row, or a refusal. */
	.spoken {
		position: absolute;
		inline-size: 1px;
		block-size: 1px;
		overflow: hidden;
		clip-path: inset(50%);
		white-space: nowrap;
		margin: 0;
	}

	@media (max-width: 700px) {
		.columns {
			grid-template-columns: minmax(0, 1fr);
		}
	}
</style>
